// React error boundary — the app previously had none, so any render-time
// throw white-screened the whole app with no way back short of a manual
// reload. Mounted at the app root (App.tsx) and around dashboard tab
// content so a crash in one area degrades to a friendly retry card.
import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { isStaleChunkError, reloadForStaleChunk } from "@/lib/staleChunkReload";

interface Props {
  children: ReactNode;
  /** Short description of the area, used in the fallback copy (e.g. "this tab"). */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    // No-op unless Sentry was initialized (production with a DSN). Reported
    // even when we recover below, so the stale-chunk rate stays visible.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    // A lazy route whose chunk a newer deploy replaced. React caught the
    // rejection here, so the global listeners in staleChunkReload never saw
    // it — recover from this side too. Falls through to the card below once
    // the reload budget is spent.
    if (isStaleChunkError(error)) reloadForStaleChunk();
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Reaching the card with a stale-chunk error means reloading did not clear
    // it, so say what is actually wrong instead of "unexpected error".
    const stale = isStaleChunkError(this.state.error);

    return (
      <div className="flex min-h-[40vh] items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="font-semibold text-lg">
              {stale ? "The app was updated" : "Something went wrong"}
            </p>
            <p className="text-sm text-muted-foreground">
              {stale ? (
                <>
                  A newer version is available and this page is still running the
                  old one. Your data is safe — reload to pick up the update.
                </>
              ) : (
                <>
                  {this.props.label ?? "This part of the page"} hit an unexpected error. Your data is
                  safe — try again, or reload the page if it keeps happening.
                </>
              )}
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={this.handleRetry} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button onClick={() => window.location.reload()}>Reload Page</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

/** Inline query-failure card with a retry action — for isError states where
 *  an empty list would otherwise be indistinguishable from "nothing here". */
export const QueryErrorCard = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <Card className="bg-lime-soft">
    <CardContent className="pt-6 text-center space-y-3">
      <p className="text-muted-foreground">{message}</p>
      <Button onClick={onRetry} variant="outline" size="sm">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);
